import { useState, useRef, useEffect } from 'react'
import { Modal, Form, Table, Button, Spinner, Alert } from 'react-bootstrap'
import api from '../../services/api'

/**
 * Modal reutilizable para buscar y seleccionar cuentas contables.
 * Muestra código, nombre, ruta completa y tipo.
 * Búsqueda con debounce, mínimo 2 caracteres.
 */
const AccountSearchModal = ({
  show,
  onHide,
  onSelect,
  clienteId = '',
  minSearchLength = 2,
  pageSize = 200,
  initialSearch = ''
}) => {
  const [busqueda, setBusqueda] = useState('')
  const [resultados, setResultados] = useState([])
  const [buscando, setBuscando] = useState(false)
  const debounceRef = useRef(null)

  // Limpiar al cerrar; si initialSearch al abrir, pre-llenar y buscar
  useEffect(() => {
    if (!show) {
      setBusqueda('')
      setResultados([])
    } else if (initialSearch) {
      const q = String(initialSearch).trim()
      setBusqueda(q || initialSearch)
      if (q.length >= minSearchLength) buscarCuentas(q)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buscarCuentas es estable
  }, [show, initialSearch])

  const buscarCuentas = async (query) => {
    if (!query || query.trim().length < minSearchLength) {
      setResultados([])
      return
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setBuscando(true)
        const params = new URLSearchParams({
          search: query.trim(),
          is_active: 'true',
          page_size: String(pageSize)
        })
        if (clienteId) {
          params.append('cliente_id', clienteId)
        }

        const response = await api.get(`/accounting/accounts/?${params.toString()}`)
        const data = response.data.results || response.data || []
        setResultados(Array.isArray(data) ? data : [])
      } catch (error) {
        console.error('Error buscando cuentas:', error)
        setResultados([])
      } finally {
        setBuscando(false)
      }
    }, 300)
  }

  const handleBusquedaChange = (e) => {
    const value = e.target.value
    setBusqueda(value)
    buscarCuentas(value)
  }

  const handleSelect = (cuenta) => {
    if (!cuenta?.id) return
    onSelect(cuenta)
    onHide()
  }

  const getAccountRealId = (account) => {
    if (typeof account.id === 'string' && account.id.startsWith('pers_')) {
      return account.cuenta_base_id ?? account.id
    }
    return account.id
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Buscar Cuenta Contable</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Buscar por código o nombre</Form.Label>
          <Form.Control
            type="text"
            placeholder={`Escribe para buscar (mínimo ${minSearchLength} caracteres)...`}
            value={busqueda}
            onChange={handleBusquedaChange}
            autoFocus
          />
        </Form.Group>

        {buscando && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Buscando...
          </div>
        )}

        {!buscando && resultados.length > 0 && (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                <tr>
                  <th style={{ width: '120px' }}>Código</th>
                  <th>Nombre</th>
                  <th style={{ minWidth: '200px' }}>Ruta completa</th>
                  <th style={{ width: '100px' }}>Tipo</th>
                  <th style={{ width: '90px' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((cuenta) => (
                  <tr key={cuenta.id || getAccountRealId(cuenta)}>
                    <td><strong>{cuenta.code}</strong></td>
                    <td>{cuenta.name}</td>
                    <td>
                      <small className="text-muted">
                        {cuenta.full_path || cuenta.code}
                      </small>
                    </td>
                    <td>
                      {cuenta.account_type && (
                        <span className="badge bg-info" style={{ fontSize: '0.75rem' }}>
                          {cuenta.account_type}
                        </span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSelect(cuenta)}
                      >
                        Seleccionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {!buscando && busqueda.trim().length >= minSearchLength && resultados.length === 0 && (
          <Alert variant="info" className="mb-0">
            No se encontraron resultados para &quot;{busqueda}&quot;
          </Alert>
        )}

        {!buscando && busqueda.trim().length > 0 && busqueda.trim().length < minSearchLength && (
          <Alert variant="secondary" className="mb-0">
            Escribe al menos {minSearchLength} caracteres para comenzar la búsqueda
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AccountSearchModal
